import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style'
import { getPointResolution } from 'ol/proj'
import { getArea } from 'ol/sphere'
import MultiPolygon from 'ol/geom/MultiPolygon'
import Polygon from 'ol/geom/Polygon'
import Point from 'ol/geom/Point'
import type OlMap from 'ol/Map'
import type Feature from 'ol/Feature'
import type { FeatureLike } from 'ol/Feature'
import type Geometry from 'ol/geom/Geometry'
import { PROXY_BASE } from '../config'
import { layerInfoOf, type WmsTileLayer } from './layers'
import { isDebugEnabled } from '../ui/debugLog'

const LABEL_PATTERNS: Array<{ pattern: string; field: string }> = [
  { pattern: 'property', field: 'prop_name' },
  { pattern: 'asset', field: 'asset_name' },
]

/**
 * Everything that distinguishes one family of labels from another. One rule per
 * asset type, plus a default for properties and for types we do not know.
 *
 * `maxScale`    Cut-off (1:N): the name disappears past it. Mirrors the icon
 *               rules of the server-side style (`Assets.sld`) so text never
 *               survives the symbol it belongs to. The SLD carries GeoServer
 *               denominators, which at ~48°N fire around 1.5× lower than the
 *               scale a client displays; the numbers here are the *client*
 *               scales its Abstract quotes, the ones this viewer measures.
 *               Operating Mine is the one departure: its icon has no cut-off at
 *               all (it only steps down 28→24→20 px), so its labels carry one of
 *               their own rather than piling 186 names onto a continental view.
 * `rank`        Who wins a collision. OpenLayers executes decluttered z-indexes
 *               in DESCENDING order (`ol/render/canvas/ExecutorGroup`:
 *               `zs.sort(declutterTree ? descending : ascending)`), so the
 *               highest rank reserves its box first. This is what makes the
 *               survivor of a crowded area the *most significant* name instead
 *               of whichever one the draw order happened to reach first.
 * `fontMin/Max` Type size in px at the cut-off / at close zoom. Deliberately
 *               small: the dense areas carry far more points than text, and size
 *               is also how the eye reads rank without knowing the categories.
 * `fill/stroke` Text colour and halo. Assets are dark type on a white halo —
 *               more legible at small sizes on a light basemap than the reverse.
 *               Colour is a *secondary* cue: with five categories no palette is
 *               fully colour-blind-safe, so rank is carried by size and survival.
 * `iconPx`      Footprint of the server-drawn icon, reserved as a declutter
 *               obstacle at `rank + 1` (see iconObstacleImage). 0 = no icon
 *               (properties).
 */
interface LabelRule {
  maxScale: number
  rank: number
  fontMin: number
  fontMax: number
  fill: string
  stroke: string
  iconPx: number
  /**
   * Scale past which GeoServer stops drawing the icon. Equal to `maxScale` for
   * every type except Operating Mine, whose icon never disappears — which is
   * exactly why the two numbers have to be kept apart: the space an icon
   * occupies must be reserved while the icon is on screen, not while its name is.
   */
  iconMaxScale: number
  /**
   * The `asset_type` value exactly as the registry spells it — the lookup keys
   * are lower-cased, this is what goes into a server-side filter. null for the
   * default rule, which stands for "any type, including ones we do not know".
   */
  value: string | null
}

/** Properties, and any asset whose type is absent or unknown. Unchanged look. */
const DEFAULT_LABEL_RULE: LabelRule = {
  maxScale: 820_000,
  // Above every asset: a property is the customer's own holding, and its name is
  // the one thing that must not be pushed off the map by an asset inside it.
  rank: 60,
  fontMin: 10,
  fontMax: 14,
  fill: '#ffffff',
  stroke: '#333333',
  iconPx: 0,
  iconMaxScale: 0,
  value: null,
}

/** Keys are lower-cased; lookup normalises the feature value the same way. */
const ASSET_LABEL_RULES: Record<string, LabelRule> = {
  'operating mine': {
    // 1:3,500,000, not the 1:2,600,000 of its first icon step: Pierre's call
    // after reading the map. The names stay useful a good deal further out than
    // the icon's own size change suggested, and the last icon step (1:3,750,000)
    // is still ahead — the label continues to stop before its symbol does.
    maxScale: 3_500_000,
    rank: 50,
    // Floor raised from 9 to 10 px so the extra range does not buy names too
    // small to read: 10 px is the size a label already had at 1:1,600,000, the
    // tranche Pierre judged right, and the ramp now holds it to the cut-off.
    fontMin: 10,
    fontMax: 12,
    fill: '#a61b1b',
    stroke: '#ffffff',
    iconPx: 28,
    iconMaxScale: Infinity,
    value: 'Operating Mine',
  },
  deposit: {
    maxScale: 1_000_000,
    rank: 40,
    fontMin: 8,
    fontMax: 11,
    fill: '#1d4ed8',
    stroke: '#ffffff',
    iconPx: 26,
    iconMaxScale: 1_000_000,
    value: 'Deposit',
  },
  'historical mine': {
    maxScale: 750_000,
    rank: 30,
    fontMin: 8,
    fontMax: 10,
    // Muted on purpose — a former producer is context, not a live site — but no
    // lighter than this: at 8 px on a green basemap it stops being readable.
    fill: '#374151',
    stroke: '#ffffff',
    iconPx: 24,
    iconMaxScale: 750_000,
    value: 'Historical Mine',
  },
  zone: {
    maxScale: 750_000,
    rank: 20,
    fontMin: 8,
    fontMax: 10,
    fill: '#0f766e',
    stroke: '#ffffff',
    iconPx: 24,
    iconMaxScale: 750_000,
    value: 'Zone',
  },
  occurrence: {
    maxScale: 371_000,
    rank: 10,
    fontMin: 8,
    fontMax: 9,
    fill: '#6b21a8',
    stroke: '#ffffff',
    iconPx: 20,
    iconMaxScale: 371_000,
    value: 'Occurrence',
  },
}

/** OGC standard pixel — 0.28 mm. Same convention as OpenLayers' scale line. */
const METERS_PER_PIXEL_AT_1_TO_1 = 0.00028

/** Scale denominator (1:N) → ground resolution (m/px), the unit the view reports. */
function scaleToResolution(scale: number): number {
  return scale * METERS_PER_PIXEL_AT_1_TO_1
}

/**
 * Widest cut-off any feature of an asset layer can claim. Used by the WFS fetch,
 * which is per layer and cannot know the types it is about to receive: stopping
 * at the default 1:820,000 would leave Operating Mine with no data to label in
 * the range its own rule opens up.
 */
const ASSET_LAYER_MAX_RESOLUTION = scaleToResolution(
  Math.max(
    DEFAULT_LABEL_RULE.maxScale,
    ...Object.values(ASSET_LABEL_RULES).map((rule) => rule.maxScale),
  ),
)

/** Reads `asset_type` case-insensitively — GeoServer casing varies. */
function assetTypeOf(feature: Feature<Geometry>): string | null {
  for (const key of feature.getKeys()) {
    if (key.toLowerCase() === 'asset_type') {
      const val = feature.get(key) as unknown
      if (val != null && val !== '') return String(val).trim().toLowerCase()
    }
  }
  return null
}

/** The rule that applies to one feature. Assets answer by type; the rest default. */
function ruleOf(feature: Feature<Geometry>, isPointLayer: boolean): LabelRule {
  if (!isPointLayer) return DEFAULT_LABEL_RULE
  const type = assetTypeOf(feature)
  return (type === null ? undefined : ASSET_LABEL_RULES[type]) ?? DEFAULT_LABEL_RULE
}

/** Feature cap per WFS label request — avoids flooding the map with text. */
const LABEL_FEATURE_LIMIT = 500

/**
 * The asset types that can still carry a name at this resolution, or null when
 * the request should not be filtered at all.
 *
 * Past the default cut-off (1:820,000) only the types with a longer reach can be
 * labelled — an unknown type falls back to the default rule and is out of range
 * by definition — so the server can be told to send those and nothing else.
 * That matters because the request is capped at LABEL_FEATURE_LIMIT features:
 * unfiltered, a wide view spends its whole budget on features whose names will
 * never be drawn, and *which* ones come back changes with every pan. Filtering
 * is what makes the answer stable, not merely smaller.
 *
 * Below the default cut-off everything is labellable and the filter is dropped —
 * including the unknown types an IN-list could not name.
 */
function labellableAssetTypes(groundRes: number): string[] | null {
  if (groundRes <= scaleToResolution(DEFAULT_LABEL_RULE.maxScale)) return null
  const types: string[] = []
  for (const rule of Object.values(ASSET_LABEL_RULES)) {
    if (rule.value !== null && groundRes <= scaleToResolution(rule.maxScale)) types.push(rule.value)
  }
  return types.length === 0 ? null : types
}

/** CQL string literal: single-quoted, inner quotes doubled. */
function cqlLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`
}

/** Halo width, as a share of type size. Thin: at 8–12 px a fat halo is a blur. */
const LABEL_HALO_RATIO = 0.15
const LABEL_HALO_MIN_PX = 1.2

/**
 * Breathing room around each label's declutter box, in px [top, right, bottom,
 * left]. Without it neighbours are merely *not overlapping*, which still reads
 * as a wall of text.
 */
const LABEL_PADDING_PX = [2, 3, 2, 3]

/** Clearance between an icon's edge and the nearest edge of its name. */
const LABEL_ICON_GAP_PX = 6

/**
 * Where a name may sit relative to its icon, tried in this order. `dx`/`dy` are
 * unit directions scaled by the icon's radius plus the gap; `align`/`baseline`
 * say which corner of the text box lands there.
 *
 * The order is the rule: straight above first — the position every label used to
 * take, so nothing moves where there is already room — then below, then the
 * sides, then the diagonals. A name only ever leaves its habitual spot because
 * that spot is taken.
 */
const PLACEMENTS: ReadonlyArray<{
  dx: number
  dy: number
  align: CanvasTextAlign
  baseline: CanvasTextBaseline
}> = [
  { dx: 0, dy: -1, align: 'center', baseline: 'bottom' },
  { dx: 0, dy: 1, align: 'center', baseline: 'top' },
  { dx: 1, dy: 0, align: 'left', baseline: 'middle' },
  { dx: -1, dy: 0, align: 'right', baseline: 'middle' },
  { dx: 0.7, dy: -0.7, align: 'left', baseline: 'bottom' },
  { dx: -0.7, dy: -0.7, align: 'right', baseline: 'bottom' },
  { dx: 0.7, dy: 0.7, align: 'left', baseline: 'top' },
  { dx: -0.7, dy: 0.7, align: 'right', baseline: 'top' },
]

/**
 * Index into PLACEMENTS chosen by the layout pass, or -1 when every position was
 * taken and the name is dropped. Absent means the pass has not run for these
 * features yet — the style then falls back to the first placement.
 */
const PLACEMENT_KEY = '_gvLabelPlacement'

/** Type size for a rule at a given ground resolution. Shared by layout and style. */
function fontSizeFor(rule: LabelRule, groundRes: number): number {
  const maxRes = scaleToResolution(rule.maxScale)
  const span = rule.fontMax - rule.fontMin
  const ramp = rule.fontMax - (groundRes / maxRes) * span
  return Math.round(Math.min(rule.fontMax, Math.max(rule.fontMin, ramp)))
}

function fontOf(size: number): string {
  return `bold ${size}px sans-serif`
}

/** Text measurement needs a canvas; one is enough for the whole module. */
const measureContext = document.createElement('canvas').getContext('2d')

function textWidthOf(text: string, font: string): number {
  if (measureContext === null) return text.length * 6 // headless fallback, never in a browser
  measureContext.font = font
  return measureContext.measureText(text).width
}

/**
 * A rectangle of taken space, with the rank that owns it. A candidate is blocked
 * only by boxes ranked *above* it — the same bargain the declutter obstacles
 * strike: an operating mine's name may cover an occurrence's icon, never the
 * other way round. Ranking icons absolutely was measured to cost 19% of the mine
 * labels, which is precisely the priority this whole exercise is about.
 */
interface Box {
  minX: number
  minY: number
  maxX: number
  maxY: number
  rank: number
}

function overlaps(a: Box, b: Box): boolean {
  return a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY
}

/**
 * Chooses where each asset name sits, so a crowded area fans its names out
 * instead of stacking them all above their points.
 *
 * Runs over the features of one viewport, in rank order: the most significant
 * name picks first and therefore keeps the position closest to its icon, and a
 * name that finds no free position is dropped rather than drawn on top of
 * something. That is also the density cap — a saturated area simply runs out of
 * room, and what it runs out of first is room for the least significant names.
 *
 * Geometry note: this works in *relative* pixels — map coordinates divided by
 * the view resolution, with an arbitrary origin. Only distances between features
 * matter for collisions, so no screen position is ever needed. That is what
 * keeps the pass immune to the trap that bit the Shapes mode (T6): opening a
 * panel resizes the map, which would move every absolute pixel but leaves every
 * distance untouched.
 */
function layoutAssetLabels(
  features: Feature<Geometry>[],
  field: string,
  viewResolution: number,
  groundRes: number,
): void {
  interface Candidate {
    feature: Feature<Geometry>
    rule: LabelRule
    px: number
    py: number
    width: number
    height: number
  }
  const candidates: Candidate[] = []
  const occupied: Box[] = []

  for (const feature of features) {
    feature.set(PLACEMENT_KEY, undefined, true)
    const rule = ruleOf(feature, true)
    const anchor = assetAnchorOf(feature.getGeometry())
    if (anchor === undefined) continue
    const [x, y] = anchor.getCoordinates()
    // Screen orientation: y grows downwards, map northing grows upwards.
    const px = x / viewResolution
    const py = -y / viewResolution

    // The icon's own space, reserved while GeoServer is drawing it — which for
    // Operating Mine outlasts its name by a long way.
    if (groundRes <= scaleToResolution(rule.iconMaxScale)) {
      const radius = rule.iconPx / 2
      occupied.push({
        minX: px - radius,
        minY: py - radius,
        maxX: px + radius,
        maxY: py + radius,
        rank: rule.rank + 1,
      })
    }

    const text = feature.get(field) as unknown
    if (text == null || text === '') continue
    if (groundRes > scaleToResolution(rule.maxScale)) continue
    const fontSize = fontSizeFor(rule, groundRes)
    candidates.push({
      feature,
      rule,
      px,
      py,
      width: textWidthOf(String(text), fontOf(fontSize)),
      height: fontSize * 1.2,
    })
  }

  candidates.sort((a, b) => b.rule.rank - a.rule.rank)

  for (const candidate of candidates) {
    let chosen = -1
    for (let i = 0; i < PLACEMENTS.length; i++) {
      const box = placementBox(candidate, PLACEMENTS[i])
      if (occupied.some((taken) => taken.rank > box.rank && overlaps(taken, box))) continue
      occupied.push(box)
      chosen = i
      break
    }
    candidate.feature.set(PLACEMENT_KEY, chosen, true)
  }
}

function placementBox(
  candidate: { px: number; py: number; width: number; height: number; rule: LabelRule },
  placement: (typeof PLACEMENTS)[number],
): Box {
  const [offsetX, offsetY] = placementOffset(candidate.rule, placement)
  const x = candidate.px + offsetX
  const y = candidate.py + offsetY
  const left =
    placement.align === 'left' ? x : placement.align === 'right' ? x - candidate.width : x - candidate.width / 2
  const top =
    placement.baseline === 'top'
      ? y
      : placement.baseline === 'bottom'
        ? y - candidate.height
        : y - candidate.height / 2
  const [padTop, padRight, padBottom, padLeft] = LABEL_PADDING_PX
  return {
    minX: left - padLeft,
    minY: top - padTop,
    maxX: left + candidate.width + padRight,
    maxY: top + candidate.height + padBottom,
    rank: candidate.rule.rank,
  }
}

/** Pixel offset of a placement: out from the icon's edge, plus the gap. */
function placementOffset(rule: LabelRule, placement: (typeof PLACEMENTS)[number]): [number, number] {
  const reach = rule.iconPx / 2 + LABEL_ICON_GAP_PX
  return [placement.dx * reach, placement.dy * reach]
}

/**
 * Set on the losing features of a group so the style skips them. One property
 * is several WFS features when its polygons are disjoint, and each would
 * otherwise draw the same name: `declutter` compares screen boxes, never text,
 * so it keeps both copies as long as they do not overlap.
 */
const LABEL_SUPPRESSED = '_gvLabelSuppressed'

/** Groups features by identity, keyed case-insensitively — GeoServer casing varies. */
const GROUP_KEY_FIELDS = ['propertyid', 'property_id']

function groupKeyOf(feature: Feature<Geometry>, field: string): string | null {
  for (const key of feature.getKeys()) {
    if (GROUP_KEY_FIELDS.includes(key.toLowerCase())) {
      const val = feature.get(key) as unknown
      if (val != null && val !== '') return `id:${String(val)}`
    }
  }
  // No identifier in the payload — fall back to the displayed name. Two distinct
  // properties sharing a name would then collapse into one label; that is the
  // known price of the fallback, and why the identifier is preferred.
  const name = feature.get(field) as unknown
  return name == null || name === '' ? null : `name:${String(name)}`
}

/**
 * Elects one label carrier per group: the largest polygon, so the name lands on
 * the parcel the eye reads as "the property". Runs on the features of the current
 * viewport, so a property whose biggest parcel is off-screen still gets labelled
 * by the biggest one in view.
 *
 * Point layers are left alone: two assets that happen to share a name are two
 * assets, not one drawn twice.
 */
function electLabelCarriers(features: Feature<Geometry>[], field: string): void {
  const best = new Map<string, { feature: Feature<Geometry>; area: number }>()
  for (const feature of features) {
    const key = groupKeyOf(feature, field)
    if (key === null) continue
    const geom = feature.getGeometry()
    const area = geom ? getArea(geom) : 0
    const current = best.get(key)
    if (current === undefined || area > current.area) {
      if (current !== undefined) current.feature.set(LABEL_SUPPRESSED, true)
      best.set(key, { feature, area })
    } else {
      feature.set(LABEL_SUPPRESSED, true)
    }
  }
}

/** Returns the label attribute field for a layer name, or null if not supported. */
export function getLabelField(layerName: string): string | null {
  const lower = layerName.toLowerCase()
  return LABEL_PATTERNS.find((c) => lower.includes(c.pattern))?.field ?? null
}

export function isLabelSupported(layerName: string): boolean {
  return getLabelField(layerName) !== null
}

/** Assets are points; properties are polygons. Only the latter are deduplicated. */
function isPointLabelField(field: string): boolean {
  return field === 'asset_name'
}

/**
 * Where to anchor the label of a multi-part geometry.
 *
 * A property with disjoint parcels is ONE feature holding a MultiPolygon, and
 * OpenLayers labels every part of it: `TextBuilder` walks `getFlatInteriorPoints()`
 * and emits the text at each (see its `case 'MultiPolygon'`). That is by design,
 * and it is why the name appeared several times even though the data holds a
 * single row. Pinning `Style.geometry` to one point overrides that walk.
 *
 * The point chosen is the interior point of the largest part, so the name lands
 * on the parcel the eye reads as "the property". Returns undefined for every
 * other geometry type — a plain Polygon already gets exactly one label.
 */
function labelAnchorOf(geometry: Geometry | undefined): Point | undefined {
  if (!(geometry instanceof MultiPolygon)) return undefined
  const polygons = geometry.getPolygons()
  if (polygons.length < 2) return undefined
  let largest = polygons[0]
  let largestArea = -1
  for (const polygon of polygons) {
    const area = polygon.getArea()
    if (area > largestArea) {
      largestArea = area
      largest = polygon
    }
  }
  // getInteriorPoint() carries an XYM layout whose M is the parcel width; keep
  // only X/Y so the renderer reads a plain 2-D point.
  const [x, y] = largest.getInteriorPoint().getCoordinates()
  return new Point([x, y])
}

export interface LabelController {
  toggle(wms: WmsTileLayer): boolean
  isEnabled(wms: WmsTileLayer): boolean
  /** Called when the parent layer row visibility changes — hides labels when the layer is off. */
  setVisible(wms: WmsTileLayer, visible: boolean): void
}

/**
 * Where to put an asset's label and its icon obstacle.
 *
 * An asset layer is expected to serve points, but nothing guarantees it, and an
 * image style is only drawn on a point geometry — a polygon would silently get
 * no obstacle at all. Always returns a point, so the layout pass can read one
 * coordinate per asset whatever the source served.
 */
function assetAnchorOf(geometry: Geometry | undefined): Point | undefined {
  if (geometry === undefined) return undefined
  if (geometry instanceof Point) return geometry
  return labelAnchorOf(geometry) ?? interiorPointOf(geometry)
}

function interiorPointOf(geometry: Geometry): Point | undefined {
  if (!(geometry instanceof Polygon)) return undefined
  const [x, y] = geometry.getInteriorPoint().getCoordinates()
  return new Point([x, y])
}

/**
 * The icons are drawn by GeoServer, inside raster tiles: OpenLayers has no idea
 * they are there, so nothing stops a label from landing squarely on a neighbour's
 * symbol. This puts an invisible disc of the icon's own size into the declutter
 * tree, at a rank above every label, so the icons' space is reserved first.
 *
 * The obstacle carries its own category's rank, not a blanket maximum. Ranking
 * every icon above every name would let a lone Occurrence symbol evict the name
 * of an operating mine — measured, and the reason a first attempt at this barely
 * moved the outcome. At `rank + 1`, an icon always beats the names of its own
 * category and below, and always yields to a more significant one.
 *
 * Cached per size — a fresh CircleStyle per feature per frame would rasterise a
 * canvas each time.
 */
const obstacleImages = new Map<number, CircleStyle>()

function iconObstacleImage(px: number): CircleStyle {
  let image = obstacleImages.get(px)
  if (image === undefined) {
    image = new CircleStyle({
      radius: px / 2,
      fill: new Fill({ color: 'rgba(0,0,0,0)' }),
      declutterMode: 'obstacle',
    })
    obstacleImages.set(px, image)
  }
  return image
}

function makeLabelStyle(
  field: string,
  map: OlMap,
): (feature: FeatureLike, resolution: number) => Style | Style[] | undefined {
  const isPointLayer = isPointLabelField(field)
  return (feature: FeatureLike, resolution: number) => {
    const val = (feature as Feature<Geometry>).get(field) as unknown
    if (val == null || val === '') return undefined
    if ((feature as Feature<Geometry>).get(LABEL_SUPPRESSED) === true) return undefined
    // Use ground resolution (latitude-corrected) so font scaling matches the displayed scale bar.
    const center = map.getView().getCenter() ?? [0, 0]
    const groundRes = getPointResolution('EPSG:3857', resolution, center)
    // The feature carries its own rule (assets: one per type, like the icons).
    // The layer is fetched down to the widest cut-off, so the ones that stop
    // earlier must drop out here, feature by feature.
    const rule = ruleOf(feature as Feature<Geometry>, isPointLayer)
    if (groundRes > scaleToResolution(rule.maxScale)) return undefined
    // Ramps from fontMax at close zoom down to fontMin at the cut-off itself.
    // Anchoring on that feature's own cut-off rather than a fixed slope is what
    // keeps the ramp spread over its whole visible range: the original slope
    // bottomed out at 80 m/px, so every zoom past that drew at the floor. Types
    // that survive longer therefore stay readable longer.
    const fontSize = fontSizeFor(rule, groundRes)
    const strokeWidth = Math.max(LABEL_HALO_MIN_PX, fontSize * LABEL_HALO_RATIO)
    const anchor = isPointLayer
      ? assetAnchorOf((feature as Feature<Geometry>).getGeometry())
      : labelAnchorOf((feature as Feature<Geometry>).getGeometry())

    // Position chosen by the layout pass. -1 means every position around this
    // icon was taken; undefined means the pass has not run yet for these
    // features, in which case the first placement is the honest guess.
    const chosen = (feature as Feature<Geometry>).get(PLACEMENT_KEY) as number | undefined
    if (isPointLayer && chosen === -1) return iconObstacleOnly(anchor, rule)
    const placement = PLACEMENTS[isPointLayer ? (chosen ?? 0) : 0]
    const [offsetX, offsetY] = isPointLayer ? placementOffset(rule, placement) : [0, 0]

    const label = new Style({
      geometry: anchor,
      zIndex: rule.rank,
      text: new Text({
        text: String(val),
        font: fontOf(fontSize),
        fill: new Fill({ color: rule.fill }),
        stroke: new Stroke({ color: rule.stroke, width: strokeWidth }),
        padding: LABEL_PADDING_PX,
        overflow: true,
        offsetX,
        offsetY,
        textAlign: isPointLayer ? placement.align : undefined,
        textBaseline: isPointLayer ? placement.baseline : 'middle',
      }),
    })
    if (!isPointLayer || rule.iconPx === 0) return label
    return [
      label,
      new Style({ geometry: anchor, zIndex: rule.rank + 1, image: iconObstacleImage(rule.iconPx) }),
    ]
  }
}

/** A dropped name still owes the map its icon's reserved space. */
function iconObstacleOnly(anchor: Point | undefined, rule: LabelRule): Style | undefined {
  if (rule.iconPx === 0) return undefined
  return new Style({ geometry: anchor, zIndex: rule.rank + 1, image: iconObstacleImage(rule.iconPx) })
}

/**
 * Label controller — labels are sourced from WFS, with the proxy enforcing access.
 * Each supported layer gets a lazily-built VectorLayer loaded on moveend via WFS GetFeature.
 * Labels are hidden above LABEL_MAX_RESOLUTION (see LABEL_MAX_SCALE).
 */
export function createLabelController(map: OlMap): LabelController {
  const enabled = new Map<WmsTileLayer, boolean>()
  const rowVisible = new Map<WmsTileLayer, boolean>()
  const labelLayers = new Map<WmsTileLayer, VectorLayer<VectorSource>>()
  /** Newest request per layer; older responses are discarded (see fetchLabels). */
  const requestTickets = new Map<WmsTileLayer, number>()

  function buildLabelLayer(wms: WmsTileLayer): VectorLayer<VectorSource> {
    const field = getLabelField(layerInfoOf(wms).name) ?? 'name'
    const layer = new VectorLayer({
      source: new VectorSource(),
      style: makeLabelStyle(field, map),
      declutter: true,
      zIndex: 20,
      visible: false,
    })
    map.addLayer(layer)
    return layer
  }

  function isRowVisible(wms: WmsTileLayer): boolean {
    return rowVisible.get(wms) ?? wms.getVisible()
  }

  function fetchLabels(wms: WmsTileLayer): void {
    const viewResolution = map.getView().getResolution() ?? Infinity
    const labelLayer = labelLayers.get(wms)
    if (!labelLayer) return
    const source = labelLayer.getSource()!

    const size = map.getSize()
    if (!size) return
    const extent = map.getView().calculateExtent(size)
    const info = layerInfoOf(wms)

    const center = map.getView().getCenter() ?? [0, 0]
    const groundResolution = getPointResolution('EPSG:3857', viewResolution, center)
    // Per-layer cut-off: the widest any of its features can claim. The style
    // function then drops the individual features that stop earlier.
    const field = getLabelField(info.name)
    const maxResolution =
      field !== null && isPointLabelField(field)
        ? ASSET_LAYER_MAX_RESOLUTION
        : scaleToResolution(DEFAULT_LABEL_RULE.maxScale)
    if (groundResolution > maxResolution) {
      source.clear()
      return
    }

    function buildUrl(types: string[] | null): string {
      const params = new URLSearchParams({
        service: 'WFS',
        version: '2.0.0',
        request: 'GetFeature',
        typenames: info.name,
        outputFormat: 'application/json',
        srsName: 'EPSG:4326',
        count: String(LABEL_FEATURE_LIMIT),
        bbox: `${extent.join(',')},EPSG:3857`,
      })
      if (types !== null) {
        params.set('cql_filter', `asset_type IN (${types.map(cqlLiteral).join(',')})`)
      }
      return `${PROXY_BASE}/geoserver/${info.workspace}/wfs?${params.toString()}`
    }

    const isAssetLayer = field !== null && isPointLabelField(field)
    const types = isAssetLayer ? labellableAssetTypes(groundResolution) : null

    // Every pan starts a request; responses can arrive out of order, and the old
    // code let a late one overwrite a fresh one — the map then showed the labels
    // of a stale extent, which is names blinking in and out as you pan. Only the
    // newest request is allowed to write to the source.
    const ticket = (requestTickets.get(wms) ?? 0) + 1
    requestTickets.set(wms, ticket)
    const isCurrent = (): boolean => requestTickets.get(wms) === ticket

    async function load(url: string): Promise<Response> {
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) throw new Error(`WFS label fetch failed: ${res.status}`)
      return res
    }

    load(buildUrl(types))
      // A server that rejects the filter must not cost us every label: fall back
      // to the plain request once, and keep the flicker rather than the blank.
      .catch(async (err: unknown) => {
        if (types === null) throw err
        console.warn('Label WFS filter rejected, retrying unfiltered:', err)
        return load(buildUrl(null))
      })
      .then(async (res) => {
        if (!isCurrent()) return
        const json: unknown = await res.json()
        const features = new GeoJSON().readFeatures(json, {
          dataProjection: 'EPSG:4326',
          featureProjection: map.getView().getProjection(),
        }) as Feature<Geometry>[]
        if (isDebugEnabled()) {
          const matched = (json as { numberMatched?: number }).numberMatched
          console.info(
            `[labels] ${info.name}: ${features.length}/${matched ?? '?'} features` +
              `${types === null ? '' : ` (types: ${types.join(', ')})`}` +
              `${features.length >= LABEL_FEATURE_LIMIT ? ' — CAPPED, subset varies with the view' : ''}`,
          )
        }
        // Elect carriers, then place the survivors, before the features are
        // added: the first render already knows which ones stay silent and where
        // each name sits — no flash of duplicates, no jump from a default spot.
        // Both read the view again rather than trusting the values captured when
        // the request left, which a pan during the round-trip would have staled.
        if (field !== null && !isPointLabelField(field)) electLabelCarriers(features, field)
        if (isAssetLayer && field !== null) {
          const nowResolution = map.getView().getResolution() ?? viewResolution
          const nowCenter = map.getView().getCenter() ?? center
          layoutAssetLabels(
            features,
            field,
            nowResolution,
            getPointResolution('EPSG:3857', nowResolution, nowCenter),
          )
        }
        source.clear()
        source.addFeatures(features)
      })
      .catch((err: unknown) => {
        if (!isCurrent()) return
        console.warn('Label WFS fetch failed, labels cleared:', err)
        source.clear()
      })
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  map.on('moveend', () => {
    if (debounceTimer !== null) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      for (const [wms] of labelLayers) {
        if (enabled.get(wms) === true && isRowVisible(wms)) fetchLabels(wms)
      }
    }, 200)
  })

  function applyVisibility(wms: WmsTileLayer): void {
    const layer = labelLayers.get(wms)
    if (!layer) return
    const show = enabled.get(wms) === true && isRowVisible(wms)
    layer.setVisible(show)
    if (show) fetchLabels(wms)
  }

  return {
    toggle(wms) {
      const next = !(enabled.get(wms) === true)
      enabled.set(wms, next)
      if (!labelLayers.has(wms)) labelLayers.set(wms, buildLabelLayer(wms))
      applyVisibility(wms)
      return next
    },
    isEnabled: (wms) => enabled.get(wms) === true,
    setVisible(wms, visible) {
      rowVisible.set(wms, visible)
      applyVisibility(wms)
    },
  }
}
